import React, { useState, useRef, useEffect } from 'react';
import Cookies from 'js-cookie';
import { setFixedAngle } from './ble';
import { BASE_URL } from '../v2Api';
import DeviceConfig from './DeviceConfig';
import CalibrationItem from './CalibrationItem';
import './CompassCalibration.css';

const CompassCalibration = ({ isConnected, compassState }) => {
  const [calibrationCount, setCalibrationCount] = useState(Cookies.get('calibrationCount') ? parseInt(Cookies.get('calibrationCount')) : 2000);
  const [backgroundCalibrationCount, setBackgroundCalibrationCount] = useState(Cookies.get('backgroundCalibrationCount') ? parseInt(Cookies.get('backgroundCalibrationCount')) : 100);
  const [axleCalibrationInitialAngle, setAxleCalibrationInitialAngle] = useState(Cookies.get('axleCalibrationInitialAngle') ? parseInt(Cookies.get('axleCalibrationInitialAngle')) : 0);
  const [axleCalibrationStep, setAxleCalibrationStep] = useState(Cookies.get('axleCalibrationStep') ? parseInt(Cookies.get('axleCalibrationStep')) : 15);
  const [axleCalibrationCount, setAxleCalibrationCount] = useState(Cookies.get('axleCalibrationCount') ? parseInt(Cookies.get('axleCalibrationCount')) : 100);
  const [fixAngle, setFixAngleValue] = useState(Cookies.get('fixAngle') ? parseInt(Cookies.get('fixAngle')) : 0);
  const [downloadAutomatically, setDownloadAutomatically] = useState(Cookies.get('downloadAutomatically') === 'true');
  const [autoApplyCalibration, setAutoApplyCalibration] = useState(Cookies.get('autoApplyCalibration') === 'true');
  const [currentVoltage, setCurrentVoltage] = useState(Cookies.get('currentVoltage') ? parseFloat(Cookies.get('currentVoltage')) : 0);
  const [dialNorthAngle, setDialNorthAngle] = useState(Cookies.get('dialNorthAngle') ? parseInt(Cookies.get('dialNorthAngle')) : 0);

  const [rawCalibrationData, setRawCalibrationData] = useState([]);
  const [backgroundCalibrationData, setBackgroundCalibrationData] = useState([]);
  const [axleCalibrationData, setAxleCalibrationData] = useState([]);
  const [calibrationResults, setCalibrationResults] = useState(null);
  const [axleCalibrationResults, setAxleCalibrationResults] = useState(null);

  const deviceConfigRef = useRef();

  useEffect(() => {
    Cookies.set('calibrationCount', calibrationCount);
    Cookies.set('backgroundCalibrationCount', backgroundCalibrationCount);
    Cookies.set('axleCalibrationInitialAngle', axleCalibrationInitialAngle);
    Cookies.set('axleCalibrationStep', axleCalibrationStep);
    Cookies.set('axleCalibrationCount', axleCalibrationCount);
    Cookies.set('fixAngle', fixAngle);
    Cookies.set('downloadAutomatically', downloadAutomatically);
    Cookies.set('autoApplyCalibration', autoApplyCalibration);
    Cookies.set('currentVoltage', currentVoltage);
    Cookies.set('dialNorthAngle', dialNorthAngle);
  }, [
    calibrationCount, backgroundCalibrationCount, axleCalibrationInitialAngle,
    axleCalibrationStep, axleCalibrationCount, fixAngle, downloadAutomatically,
    autoApplyCalibration, currentVoltage, dialNorthAngle
  ]);

  const handleFixAngle = async (angle) => {
    console.log('Setting fixed angle to:', angle);
    await setFixedAngle(angle);
  };

  const handleCalculateCalibration = async (applyAndSave = false) => {
    console.log('Calculating calibration with raw data:', rawCalibrationData);
    const formattedData = rawCalibrationData.map(data => [data.x, data.y, data.z, data.angle]);

    const response = await fetch(`${BASE_URL}/calibrate_compass`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ raw_data: formattedData })
    });

    if (response.ok) {
      const result = await response.json();
      console.log('Calibration result:', result);
      setCalibrationResults(result.calibration);
      if (applyAndSave) {
        handleApplyCalibration(result.calibration);
        handleSaveConfig();
      }
    } else {
      console.error('Error calculating calibration:', response.statusText);
    }
  };

  const handleCalculateAxleCalibration = async (applyAndSave = false) => {
    console.log('Calculating axle calibration with data:');
    console.log('Axle Data:', axleCalibrationData);
    console.log('Environment Data:', backgroundCalibrationData);

    const axleDataFormatted = axleCalibrationData.map(data => [data.x, data.y, data.z, data.angle]);
    const environmentDataFormatted = backgroundCalibrationData.map(data => [data.x, data.y, data.z, data.angle]);

    const response = await fetch(`${BASE_URL}/calibrate_compass`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ axle_data: axleDataFormatted, environment_data: environmentDataFormatted })
    });

    if (response.ok) {
      const result = await response.json();
      console.log('Axle Calibration result:', result);
      setAxleCalibrationResults(result.axle_calibration);
      if (applyAndSave) {
        handleApplyAxleCalibration(result.axle_calibration);
        handleSaveConfig();
      }
    } else {
      console.error('Error calculating axle calibration:', response.statusText);
    }
  };

  const handleApplyCalibration = (calibration) => {
    const deviceConfig = deviceConfigRef.current.getDeviceConfig();
    deviceConfigRef.current.setDeviceConfig({
      ...deviceConfig,
      calibration
    });
  };

  const handleApplyAxleCalibration = (axleCalibration) => {
    const deviceConfig = deviceConfigRef.current.getDeviceConfig();
    deviceConfigRef.current.setDeviceConfig({
      ...deviceConfig,
      axleCalibration
    });
  };

  const handleSaveConfig = () => {
    deviceConfigRef.current.handleSaveConfig();
  };

  const handleCalibrationComplete = () => {
    if (autoApplyCalibration) {
      handleCalculateCalibration(true);
    }
  };

  const handleAxleCalibrationComplete = () => {
    if (autoApplyCalibration) {
      handleCalculateAxleCalibration(true);
    }
  };

  const handleResetCalibration = () => {
    const resetCalibration = [0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1];
    const resetAxleCalibration = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    const deviceConfig = deviceConfigRef.current.getDeviceConfig();

    deviceConfigRef.current.setDeviceConfig({
      ...deviceConfig,
      calibration: resetCalibration,
      axleCalibration: resetAxleCalibration,
      encoderZeroDialNorth: 0,
      compassPivot: 0
    });

    if (autoApplyCalibration) {
      setTimeout(()=> {
        handleSaveConfig()
      });
    }

    setAxleCalibrationData([]);
    setRawCalibrationData([]);
    setBackgroundCalibrationData([]);
    setAxleCalibrationResults([]);
    setCalibrationResults([]);
    setAxleCalibrationInitialAngle(0);
  };

  const getDeviceName = () => {
    const deviceConfig = deviceConfigRef.current && deviceConfigRef.current.getDeviceConfig();
    return deviceConfig?.name || 'device';
  };

  const handleAngleChange = (newAngle) => {
    setAxleCalibrationInitialAngle(newAngle);
  };

  const handleApplyVoltageCorrection = () => {
    const deviceConfig = deviceConfigRef.current.getDeviceConfig();
    const readingVoltage = compassState.batteryVoltage / (1 + deviceConfig.batteryVoltageCorrection);
    const batteryVoltageCorrection = (currentVoltage / readingVoltage - 1).toFixed(3);

    deviceConfigRef.current.setDeviceConfig({
      ...deviceConfig,
      batteryVoltageCorrection
    });

    handleSaveConfig();
  };

  const handleCalibratePivot = async () => {
    const startTime = Date.now();
    let sumMagnetometer = 0;
    let count = 0;

    const sampleMagnetometer = () => {
      if (Date.now() - startTime < 3000) {
        sumMagnetometer += compassState.magnetometer;
        count += 1;
        requestAnimationFrame(sampleMagnetometer);
      } else {
        const averageMagnetometer = Math.round(sumMagnetometer / count);
        const deviceConfig = deviceConfigRef.current.getDeviceConfig();
        deviceConfigRef.current.setDeviceConfig({
          ...deviceConfig,
          compassPivot: averageMagnetometer
        });
        handleSaveConfig();
      }
    };

    sampleMagnetometer();
  };

  const handleCalibrateEncoder = async () => {
    const deviceConfig = deviceConfigRef.current.getDeviceConfig();
    let encoderZeroDialNorth = parseInt(dialNorthAngle) + (deviceConfig.encoderZeroDialNorth || 0);
    if (encoderZeroDialNorth >= 360) encoderZeroDialNorth -= 360;

    console.log("encoderZeroDialNorth", encoderZeroDialNorth, dialNorthAngle, deviceConfig.encoderZeroDialNorth);
    
    deviceConfigRef.current.setDeviceConfig({
      ...deviceConfig,
      encoderZeroDialNorth
    });


    setTimeout(() => {
      handleSaveConfig();
    });
    setTimeout(() => {
      setFixedAngle(-1);
    }, 5000); // Set a timer for 5 seconds before releasing the fixed angle
  };

  const handleFixCurrentPosition = () => {
    const deviceConfig = deviceConfigRef.current.getDeviceConfig();
    const currentDialValue = deviceConfig.dialNorthAngle || 0;
    setFixAngleValue(currentDialValue);
    handleFixAngle(currentDialValue);
  };

  return (
    <div className="compass-calibration-container">
      <DeviceConfig
        ref={deviceConfigRef}
        isConnected={isConnected}
      />

      <h1>Set fixed angle</h1>
      <div>
        <label>Angle: <input type="text" value={fixAngle} onChange={(e) => setFixAngleValue(parseInt(e.target.value))} /></label> - which number should point north<br />
        <button onClick={() => handleFixAngle(fixAngle)}>Fix dial angle</button>
        <button onClick={handleFixCurrentPosition}>Fix current position</button>
        <button onClick={async () => await setFixedAngle(-1)}>Release dial</button><br />
      </div>

      <h1>Calibration</h1>
      <div>
        <label>
          Download Automatically: 
          <input 
            type="checkbox" 
            checked={downloadAutomatically} 
            onChange={(e) => setDownloadAutomatically(e.target.checked)} 
          />
        </label><br />
        <label>
          Automatically apply calibration results: 
          <input 
            type="checkbox" 
            checked={autoApplyCalibration} 
            onChange={(e) => setAutoApplyCalibration(e.target.checked)} 
          />
        </label><br />
        <button onClick={handleResetCalibration}>Reset Calibration</button><br />
      </div>

      <div className='manual'>
        <h2>Calibration procedure:</h2>
        <ol>
          <li><b>Tune motor:</b>
            <ol>
              <li> Set fixed angle to 0, check that motorspeed = 90 </li>
              <li> with screwdriver tune the motor to stay still </li>
              <li> <b>TODO</b> set fixed speed for motor to simplify tuning</li>
              <li> <b>TODO</b> software zero-finder for speed</li>
            </ol>
            <hr/>
          </li>
          <li>
            <b>Raw data calibration:</b>  <br/>
            Without axle and dial, place in calibrator <br/>
                <label>Readings: <input type="number" value={calibrationCount} onChange={(e) => setCalibrationCount(parseInt(e.target.value))} /></label><br />
        
                <CalibrationItem
                  rawCalibrationData={rawCalibrationData}
                  setRawCalibrationData={setRawCalibrationData}
                  filename={`rawCalibrationData_${getDeviceName()}.csv`}
                  downloadAutomatically={downloadAutomatically}
                  initialCalibrationAngle={0}
                  calibrationStep={0}
                  calibrationCount={calibrationCount}
                  onComplete={handleCalibrationComplete}
                />
                {rawCalibrationData.length > 0 && (
                  <button onClick={() => handleCalculateCalibration(false)}>Calculate Raw Calibration</button>
                )}
                {calibrationResults && (
                  <div id="calibration-results">
                    <h2>Calibration Results</h2>
                    <p>{calibrationResults.join(', ')}</p>
                    <button onClick={() => handleApplyCalibration(calibrationResults)}>Apply Calibration</button>
                  </div>
                )}
              <hr/>
          </li>
          <li>
            <b>Background field calibration: </b> <br/>
            Without axle and dial, secure in a fixed frame <br/>
                <label>Readings: <input type="number" value={backgroundCalibrationCount} onChange={(e) => setBackgroundCalibrationCount(parseInt(e.target.value))} /></label><br />
        
                <CalibrationItem
                  rawCalibrationData={backgroundCalibrationData}
                  setRawCalibrationData={setBackgroundCalibrationData}
                  filename={`backgroundCalibrationData_${getDeviceName()}.csv`}
                  downloadAutomatically={downloadAutomatically}
                  initialCalibrationAngle={0}
                  calibrationStep={0}
                  calibrationCount={backgroundCalibrationCount}
                />
            <hr/>
          </li>
          <li>
            <b>Axle calibration:</b> <br/>
            Install axle and dial, secure in the same fixed frame<br/>
                <label>Initial Angle: <input type="number" value={axleCalibrationInitialAngle} onChange={(e) => setAxleCalibrationInitialAngle(parseInt(e.target.value))} /></label><br />
                <label>Step: <input type="number" value={axleCalibrationStep} onChange={(e) => setAxleCalibrationStep(parseInt(e.target.value))} /></label><br />
                <label>Readings: <input type="number" value={axleCalibrationCount} onChange={(e) => setAxleCalibrationCount(parseInt(e.target.value))} /></label><br />
        
                <CalibrationItem
                  rawCalibrationData={axleCalibrationData}
                  setRawCalibrationData={setAxleCalibrationData}
                  filename={`axleCalibrationData_${getDeviceName()}.csv`}
                  downloadAutomatically={downloadAutomatically}
                  initialCalibrationAngle={axleCalibrationInitialAngle}
                  calibrationStep={axleCalibrationStep}
                  calibrationCount={axleCalibrationCount}
                  onComplete={handleAxleCalibrationComplete}
                  onAngleChange={handleAngleChange} // Pass the callback
                />
                {backgroundCalibrationData.length > 0 && axleCalibrationData.length > 0 && (
                  <button onClick={() => handleCalculateAxleCalibration(false)}>Calculate Axle Calibration</button>
                )}
                {axleCalibrationResults && (
                  <div id="axle-calibration-results">
                    <h2>Axle Calibration Results</h2>
                    <p>{axleCalibrationResults.join(', ')}</p>
                    <button onClick={() => handleApplyAxleCalibration(axleCalibrationResults)}>Apply Axle Calibration</button>
                  </div>
                )}
            <hr/>
          </li>
          <li>
            <b>TODO: fine-tuning</b>
            <ul>
              <li>What if it's clear that at some angles there is an error? how can we tune minor parameters?</li>
              <li>So, web page knows calibration parameters. For each encoder value it can calculate axle correction, so it can print the value at each point of rotation</li>
              <li>Maybe we can add slider to each calibration parameter?</li>
              <li>Problem - we have a lot of dimensions here: real heading, encoder value, background. Correction need to work well for all parameters</li>
            </ul>
          </li>
          <li>
            <b>Compass pivot</b> <br/>
            Align compass base with North <br/>
            <button onClick={handleCalibratePivot}>Calibrate pivot</button>
          </li>
          <li>
            <b>Encoder zero dial north - Dial pivot</b><br/>
            <button onClick={() => handleFixAngle(0)}>Fix dial position to 0</button> <br/>
            <label>Which dial angle points North? <input type="number" value={dialNorthAngle} onChange={(e) => setDialNorthAngle(parseInt(e.target.value))} /></label><br/>
            <button onClick={handleCalibrateEncoder}>Calibrate</button>
          </li>
          <li>
            <b>Battery voltage correction</b><br/>
            Enter current voltage: <input type="number" value={currentVoltage} onChange={(e) => setCurrentVoltage(parseFloat(e.target.value))} /><br/>
            <button onClick={handleApplyVoltageCorrection}>Apply</button>
          </li>
          <li>
            <b>Hall sensor threshold (optional)</b>
            <ol>
              <li>Install board into compass</li>
              <li>Install magnets</li>
              <li>Compare hall value when opened and closed</li>
              <li>Tune the threshold value</li>
            </ol>
          </li>
        </ol>
      </div>
    </div>
  );
};

export default CompassCalibration;
